import type { CartUI } from '@libs/common-node';

import { IS_SSR } from '../constants';

type CartDiscountCode = CartUI['discountCodes'][number];

const CLIENT_INELIGIBLE_DISCOUNT_CODES_STORAGE_KEY = 'cart-client-ineligible-discount-codes';
const CLIENT_INELIGIBLE_ID_PREFIX = 'client-ineligible-';

export const isClientIneligibleDiscountCodeId = (id: string | undefined): boolean =>
    id?.startsWith(CLIENT_INELIGIBLE_ID_PREFIX) ?? false;

export const isClientIneligibleDiscountCode = (discountCode: CartDiscountCode): boolean =>
    isClientIneligibleDiscountCodeId(discountCode.id);

/**
 * Recovers the original coupon code from a client-ineligible id. Ids that lack the prefix keep all of
 * their characters and are only uppercased, so this is safe to call without checking the id first.
 */
export const getCodeFromClientIneligibleDiscountCodeId = (id: string): string =>
    (isClientIneligibleDiscountCodeId(id) ? id.slice(CLIENT_INELIGIBLE_ID_PREFIX.length) : id).toUpperCase();

const normalizeDiscountCode = (code: string | null | undefined): string => code?.toUpperCase() ?? '';

const getClientIneligibleStorageKey = (cartId: string): string =>
    `${CLIENT_INELIGIBLE_DISCOUNT_CODES_STORAGE_KEY}:${cartId}`;

/**
 * localStorage is the single source of truth for which discount codes are client-ineligible for the
 * current cart. It survives page refreshes, unlike React state.
 */
export const getClientIneligibleCodesFromStorage = (cartId: string): string[] => {
    if (IS_SSR) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(
            globalThis.localStorage.getItem(getClientIneligibleStorageKey(cartId)) ?? '[]'
        ) as unknown;
        return Array.isArray(parsedValue)
            ? parsedValue.filter((code): code is string => typeof code === 'string').map(code => code.toUpperCase())
            : [];
    } catch {
        return [];
    }
};

export const setClientIneligibleCodesInStorage = (cartId: string | undefined, codes: string[]): void => {
    if (!cartId || IS_SSR) {
        return;
    }

    const uniqueCodes = [...new Set(codes.map(normalizeDiscountCode).filter(Boolean))];

    try {
        if (uniqueCodes.length === 0) {
            globalThis.localStorage.removeItem(getClientIneligibleStorageKey(cartId));
            return;
        }

        globalThis.localStorage.setItem(getClientIneligibleStorageKey(cartId), JSON.stringify(uniqueCodes));
    } catch {
        // localStorage can fail in restricted browser contexts; cart state still works for the current session.
    }
};

export const createClientIneligibleDiscountCode = (code: string): CartDiscountCode => {
    const codeUpper = code.toUpperCase();

    return {
        id: `${CLIENT_INELIGIBLE_ID_PREFIX}${codeUpper}`,
        code: codeUpper,
        name: codeUpper,
        description: '',
        isActive: true,
        state: 'DoesNotMatchCart',
    };
};

const getClientIneligibleCodesFromCart = (cart?: CartUI | null): string[] =>
    (cart?.discountCodes ?? [])
        .filter(isClientIneligibleDiscountCode)
        .map(discountCode => normalizeDiscountCode(discountCode.code))
        .filter(Boolean);

const mergeCodesIntoStorage = (cartId: string, codes: string[]): void => {
    if (codes.length === 0) {
        return;
    }

    const existingCodes = getClientIneligibleCodesFromStorage(cartId);
    const mergedCodes = [...new Set([...existingCodes, ...codes])];

    if (mergedCodes.length !== existingCodes.length) {
        setClientIneligibleCodesInStorage(cartId, mergedCodes);
    }
};

/**
 * Restores client-ineligible discount codes onto `nextCart` when the server drops them, using
 * localStorage (seeded from `nextCart`/`previousCart`) as the source of truth for what to restore.
 */
export const restoreClientIneligibleCodes = (nextCart: CartUI, previousCart?: CartUI | null): CartUI => {
    mergeCodesIntoStorage(nextCart.id, getClientIneligibleCodesFromCart(nextCart));
    if (previousCart?.id === nextCart.id) {
        mergeCodesIntoStorage(nextCart.id, getClientIneligibleCodesFromCart(previousCart));
    }

    const storedCodes = getClientIneligibleCodesFromStorage(nextCart.id);
    if (storedCodes.length === 0) {
        return nextCart;
    }

    const serverCodes = new Set(
        (nextCart.discountCodes ?? [])
            .filter(discountCode => !isClientIneligibleDiscountCode(discountCode))
            .map(discountCode => normalizeDiscountCode(discountCode.code))
    );
    const codesToRestore = storedCodes.filter(code => !serverCodes.has(code));
    setClientIneligibleCodesInStorage(nextCart.id, codesToRestore);

    const nextDiscountCodes = nextCart.discountCodes ?? [];
    const nextCodes = new Set(nextDiscountCodes.map(discountCode => normalizeDiscountCode(discountCode.code)));
    const missingCodes = codesToRestore.filter(code => !nextCodes.has(code)).map(createClientIneligibleDiscountCode);

    if (missingCodes.length === 0) {
        return nextCart;
    }

    return { ...nextCart, discountCodes: [...nextDiscountCodes, ...missingCodes] };
};
